import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { Pressable } from 'react-native';
import { Text } from '@/components/ui/text';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Trash2,
  Pencil,
  MoreVertical,
  Repeat,
  ChevronUp,
  ChevronDown,
  ChevronRight,
} from 'lucide-react-native';
import { Icon } from '@/components/ui/icon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useLocalizedValue } from '@/hooks/useLocalizedValue';
import { useDeleteProtection } from '~/global/hooks/useDeleteProtection';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useModal } from '@/contexts/ModalContext';
import { SportActivityFormDialog } from '~/sport-activity/components/sport-activity-form-dialog/SportActivityFormDialog';
import { SportActivitySelectorModal } from '../sport-activity-selector-modal/SportActivitySelectorModal';
import { db } from '@/lib/local-api';
import { SetsDimensionTable } from '../workout-sets/SetsDimensionTable';
import type {
  WorkoutExerciseRecord,
  SportActivityRecord,
} from '~/powersync/generated/schema';

interface WorkoutExerciseProps {
  exercise: WorkoutExerciseRecord;
  sportActivity: SportActivityRecord;
  className?: string;
  onDelete?: () => void;
  children?: React.ReactNode;
  /** Optional set-summary subtitle, e.g. "3 série · 12 opak · 15-20 kg". */
  summary?: string;
  /** When true, the body (sets + note) collapses behind a chevron toggle. */
  collapsible?: boolean;
  /** Initial expanded state when `collapsible`. Defaults to collapsed. */
  defaultExpanded?: boolean;
}

export const WorkoutExercise: React.FC<WorkoutExerciseProps> & {
  Synced: typeof WorkoutExerciseSynced;
} = ({
  exercise,
  sportActivity,
  className,
  onDelete,
  children,
  summary,
  collapsible = false,
  defaultExpanded,
}) => {
  const { t } = useTranslation('workout');
  const { showModal } = useModal();
  const localizedName = useLocalizedValue(sportActivity.name);
  const powerSync = usePowerSync();
  const [expanded, setExpanded] = useState(
    collapsible ? (defaultExpanded ?? false) : true,
  );
  const showBody = !collapsible || expanded;

  // Clean local-api mutation for deleting workout exercise
  const deleteExerciseMutation = useMutation({
    mutationFn: (exerciseId: string) =>
      db.Workout.mutations.softDeleteExercise(powerSync, exerciseId),
  });

  // Clean local-api mutation for swapping sport activity
  const swapExerciseMutation = useMutation({
    mutationFn: (newSportActivityId: string) =>
      db.Workout.mutations.swapExerciseSportActivity(
        powerSync,
        exercise.id,
        newSportActivityId,
      ),
  });

  // Debounced + on-blur save for the exercise note/comment
  const updateNoteMutation = useMutation({
    mutationFn: (note: string) =>
      db.Workout.mutations.updateExercise(powerSync, exercise.id, { note }),
  });

  const [note, setNote] = useState(exercise.note ?? '');
  const noteDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep local state in sync when the persisted note changes externally
  useEffect(() => {
    setNote(exercise.note ?? '');
  }, [exercise.note]);

  // Clear any pending debounce on unmount
  useEffect(() => {
    return () => {
      if (noteDebounceRef.current) {
        clearTimeout(noteDebounceRef.current);
      }
    };
  }, []);

  const handleNoteChange = (text: string) => {
    setNote(text);
    if (noteDebounceRef.current) {
      clearTimeout(noteDebounceRef.current);
    }
    noteDebounceRef.current = setTimeout(() => {
      updateNoteMutation.mutate(text);
    }, 500);
  };

  const handleNoteBlur = () => {
    if (noteDebounceRef.current) {
      clearTimeout(noteDebounceRef.current);
      noteDebounceRef.current = null;
    }
    if (note !== (exercise.note ?? '')) {
      updateNoteMutation.mutate(note);
    }
  };

  // Clean local-api mutations for moving exercises
  const moveExerciseUpMutation = useMutation({
    mutationFn: () =>
      db.Workout.mutations.moveExerciseUp(powerSync, exercise.id),
  });

  const moveExerciseDownMutation = useMutation({
    mutationFn: () =>
      db.Workout.mutations.moveExerciseDown(powerSync, exercise.id),
  });

  // Fetch all exercises in the superset to determine position
  const { data: supersetExercises } = useLivePowerSyncQuery(
    db.Workout.queries.findExercisesBySuperset(exercise.supersetId),
    [exercise.supersetId],
  );

  const isFirstExercise = exercise.sortIndex === 0;
  const isLastExercise =
    supersetExercises && exercise.sortIndex === supersetExercises.length - 1;

  const { dispatchConfirmationDialog } = useDeleteProtection(() =>
    deleteExerciseMutation.mutate(exercise.id),
  );

  const handleEdit = () => {
    if (sportActivity.userId) {
      showModal(SportActivityFormDialog, {
        sportActivityId: sportActivity.id,
      });
    }
  };

  const handleSwap = () => {
    showModal(SportActivitySelectorModal, {
      onSportActivitySelected: (newActivity: SportActivityRecord) => {
        swapExerciseMutation.mutate(newActivity.id);
      },
    });
  };

  const isCustomActivity = !!sportActivity.userId;

  return (
    <View className={cn('py-3', className)}>
      <View
        className={cn(
          'flex-row items-center justify-between',
          showBody && 'mb-3',
        )}
      >
        <Pressable
          className="flex-row items-center gap-2 flex-1"
          onPress={collapsible ? () => setExpanded((v) => !v) : undefined}
          disabled={!collapsible}
        >
          {collapsible && (
            <Icon
              as={expanded ? ChevronDown : ChevronRight}
              size={18}
              className="text-text-secondary"
            />
          )}
          <View className="flex-1">
            <Text className="font-medium">{localizedName}</Text>
            {summary ? (
              <Text className="text-xs text-text-secondary mt-0.5">
                {summary}
              </Text>
            ) : null}
          </View>
        </Pressable>
        <View className="flex-row items-center gap-3">
          {isCustomActivity && (
            <Pressable
              onPress={handleEdit}
              className="p-2 -m-2 opacity-70 hover:opacity-100"
            >
              <Icon as={Pencil} size={16} className="text-text-secondary" />
            </Pressable>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Pressable className="p-2 -m-2 opacity-70 hover:opacity-100">
                <Icon
                  as={MoreVertical}
                  size={16}
                  className="text-text-secondary"
                />
              </Pressable>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onPress={() => moveExerciseUpMutation.mutate()}
                disabled={isFirstExercise || moveExerciseUpMutation.isPending}
              >
                <Icon as={ChevronUp} size={16} />
                <Text>{t('exercise.moveUp')}</Text>
              </DropdownMenuItem>
              <DropdownMenuItem
                onPress={() => moveExerciseDownMutation.mutate()}
                disabled={isLastExercise || moveExerciseDownMutation.isPending}
              >
                <Icon as={ChevronDown} size={16} />
                <Text>{t('exercise.moveDown')}</Text>
              </DropdownMenuItem>
              <DropdownMenuItem
                onPress={handleSwap}
                disabled={swapExerciseMutation.isPending}
              >
                <Icon as={Repeat} size={16} />
                <Text>{t('exercise.swap')}</Text>
              </DropdownMenuItem>
              <DropdownMenuItem
                onPress={dispatchConfirmationDialog}
                disabled={deleteExerciseMutation.isPending}
                variant="destructive"
              >
                <Icon as={Trash2} size={16} className="text-destructive" />
                <Text>{t('exercise.delete')}</Text>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </View>
      </View>
      {showBody && (
        <>
          {children && <View>{children}</View>}
          <Input
            value={note}
            onChangeText={handleNoteChange}
            onBlur={handleNoteBlur}
            multiline
            placeholder={t('form.fields.note.placeholder')}
            textAlignVertical="top"
            className="bg-background rounded-lg px-3 py-2 border-0 text-sm text-text-primary mt-3"
            placeholderClassName="text-text-muted"
          />
        </>
      )}
    </View>
  );
};

interface WorkoutExerciseSyncedProps {
  /**
   * The exercise ID
   */
  id: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Callback when exercise is deleted
   */
  onDelete?: () => void;
}

const WorkoutExerciseSynced: React.FC<WorkoutExerciseSyncedProps> = ({
  id,
  className,
  onDelete,
}) => {
  const { t } = useTranslation('workout');

  // Fetch workout exercise using clean local-api
  const {
    data: workoutExercises,
    isLoading: isLoadingExercise,
    error: exerciseError,
  } = useLivePowerSyncQuery(db.Workout.queries.findExerciseById(id), [id]);

  const exercise = workoutExercises?.[0];

  // Fetch sport activity using clean local-api
  const {
    data: sportActivities,
    isLoading: isLoadingSportActivity,
    error: sportActivityError,
  } = useLivePowerSyncQuery<SportActivityRecord>(
    db.SportActivity.queries.findById(exercise?.sportActivityId),
    [exercise?.sportActivityId],
  );

  const sportActivity = sportActivities?.[0];

  const isLoading = isLoadingExercise || isLoadingSportActivity;
  const hasError = Boolean(exerciseError || sportActivityError);
  // The exercise or its linked SportActivity may simply not have synced into
  // the local DB yet — the live query resolves to empty with no error. That is
  // a loading state, not a failure: show a skeleton and reserve the error for a
  // genuine query error (a persistent failure).
  const isPending = isLoading || !exercise?.workoutId || !sportActivity;

  if (hasError) {
    return (
      <View testID="workout-exercise-error" className={cn('py-3', className)}>
        <Text className="text-destructive">Error loading exercise</Text>
      </View>
    );
  }

  if (isPending) {
    return (
      <View
        testID="workout-exercise-skeleton"
        className={cn('py-3', className)}
      >
        <View className="h-3 w-20 bg-muted rounded mb-2" />
        <View className="h-5 w-40 bg-muted rounded mb-3" />
        <View className="h-12 w-full bg-muted rounded" />
      </View>
    );
  }

  return (
    <WorkoutExercise
      exercise={exercise}
      sportActivity={sportActivity}
      className={className}
      onDelete={onDelete}
    >
      <SetsDimensionTable exerciseId={id} workoutId={exercise.workoutId} />
    </WorkoutExercise>
  );
};

// Attach Synced as a static property
WorkoutExercise.Synced = WorkoutExerciseSynced;
